import { uniq } from "lodash";
import { getValidLocale } from "@backpacker/intl";
import { tr as lkf18n } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { tr } from "@radarly/i18n";
import { getDateFromClause } from "../clause/getter";
import { FormatedFacet, FullFacet } from "../facets";
import {
  SearchFragment,
  SearchFragmentClassification,
  SearchFragmentDateMetrics,
  SearchFragmentDateTerm,
  SearchFragmentGeo,
  SearchFragmentPivot,
  SearchFragmentTerm,
  getTermFromAggs,
  isSearchFragmentClassification,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
  isSearchFragmentGeo,
  isSearchFragmentPivot,
  isSearchFragmentTerm,
} from "../search";

type Table = Array<string[]>;

const getSlashDate = (canonicalLocale: string, standardDate: Date) => {
  return new Intl.DateTimeFormat(canonicalLocale).format(standardDate);
};

// Some facets return more data than the chart displays.
// This is because these charts iterate over the default facet values instead of the data returned by the API.
// To ensure a consistent user experience, we need to check if a key is included in the facet.
// This limitation is only present for certain facets.
const isLimitedFacetKey = (
  facets: Record<string, FormatedFacet>,
  facet: Facet,
  key: string
) => {
  const isLimited = [
    Facet.Languages,
    Facet.ImageRecognitionAge,
    Facet.ImageRecognitionEmotions,
    Facet.ImageRecognitionGenders,
  ].includes(facet);
  if (isLimited) {
    return !facets[facet].values.some(({ id }) => id === key);
  }
  return false;
};

const getTermLabel = (
  facet: FullFacet,
  term: string,
  label?: string
): string => {
  if (label) {
    return label;
  } else {
    const validTerm =
      facet?.id === Facet.Focuses ? Number(term.replace("focus_", "")) : term;
    return facet?.values.find(({ id }) => id === validTerm)?.label || term;
  }
};

const getEvolutionPeriodLabel = (
  locale: Locale,
  evolutionFragment: SearchFragment,
  metric: string
): string => {
  const { from } = getDateFromClause(evolutionFragment.request.query);
  const periodType = from.includes("now-1y")
    ? "PREVIOUS_YEAR"
    : "PREVIOUS_PERIOD";
  return tr(locale, `INSIGHT_PAGE::METRIC::${periodType}`, {
    metric: lkf18n(locale, `METRICS::${metric}`),
  });
};

const formatDateMetricsCard = (
  locale: Locale,
  fragment: SearchFragmentDateMetrics,
  evolutionFragment?: SearchFragmentDateMetrics
): BlobPart => {
  const canonicalLocale = getValidLocale(locale);
  const aggs = fragment.data.aggs;
  const metrics = fragment.request.aggs.date.metrics;
  const table: Table = [
    [
      tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_DATE"),
      ...metrics.map((metric) => lkf18n(locale, `METRICS::${metric}`)),
    ],
  ];

  if (evolutionFragment) {
    table[0].push(
      getEvolutionPeriodLabel(locale, evolutionFragment, metrics[0])
    );
  }

  for (const [i, { key, counts }] of aggs.date.values.entries()) {
    const row: string[] = [
      getSlashDate(canonicalLocale, new Date(key)),
      ...metrics.map((metric) => String(counts[metric])),
    ];
    if (isSearchFragmentDateMetrics(evolutionFragment)) {
      const evolution =
        evolutionFragment.data.aggs.date.values[i].counts[metrics[0]];
      row.push(String(evolution));
    }
    table.push(row);
  }

  return table.join("\n");
};

const formatDateTermCard = (
  locale: Locale,
  facets: Record<string, FormatedFacet>,
  fragment: SearchFragmentDateTerm
): BlobPart => {
  const canonicalLocale = getValidLocale(locale);
  const aggs = fragment.data.aggs;
  const [metric] = fragment.request.aggs.metrics.metrics;
  const facet = getTermFromAggs(fragment.request.aggs.date.aggs);
  const allTerms =
    aggs?.date.values.flatMap((value) =>
      value.aggs[facet]?.values.map(({ key }) => key)
    ) || [];
  const terms = uniq(allTerms);
  const table: Table = [
    [tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_DATE")],
  ];
  terms.forEach((term) => table[0].push(getTermLabel(facets[facet], term)));
  for (const { key, aggs: subAggs } of aggs.date.values) {
    const row: string[] = [getSlashDate(canonicalLocale, new Date(key))];
    const values = subAggs[facet]?.values || [];
    terms.forEach((term) => {
      const termCount =
        values.find((v) => v.key === term)?.counts[metric] ?? "N/A";
      row.push(String(termCount));
    });
    table.push(row);
  }
  return table.join("\n");
};

const formatGeoCard = (
  locale: Locale,
  facets: Record<string, FormatedFacet>,
  fragment: SearchFragmentGeo,
  evolutionFragment?: SearchFragmentGeo
): BlobPart => {
  const aggs = fragment.data.aggs;
  const [metric] = fragment.request.aggs.metrics.metrics;
  const facet = getTermFromAggs(fragment.request.aggs);

  const table: Table = [
    [facets?.[facet]?.label, lkf18n(locale, `METRICS::${metric}`)],
  ];

  if (evolutionFragment) {
    table[0].push(getEvolutionPeriodLabel(locale, evolutionFragment, metric));
  }

  for (const { key, counts, location } of aggs[facet].values) {
    if (isLimitedFacetKey(facets, facet as Facet, key)) {
      continue;
    } else {
      const row = [
        getTermLabel(facets[facet], key, location?.name),
        String(counts[metric]),
      ];
      if (isSearchFragmentGeo(evolutionFragment)) {
        const evolution =
          evolutionFragment.data.aggs[facet].values.find(
            (value) => value.key === key
          )?.counts[metric] || "N/A";
        row.push(evolution.toString());
      }
      table.push(row);
    }
  }
  return table.join("\n");
};

const formatTermOrClassificationCard = <
  F extends SearchFragmentClassification | SearchFragmentTerm
>(
  locale: Locale,
  facets: Record<string, FormatedFacet>,
  fragment: F,
  evolutionFragment?: F
): BlobPart => {
  const aggs = fragment.data.aggs;
  const facet = getTermFromAggs(fragment.request.aggs);
  const metrics = fragment.request.aggs[facet].metrics as Metric[];
  const table: Table = [
    [
      facets?.[facet]?.label,
      ...metrics.map((metric) => lkf18n(locale, `METRICS::${metric}`)),
    ],
  ];

  if (evolutionFragment) {
    metrics.forEach((metric) =>
      table[0].push(getEvolutionPeriodLabel(locale, evolutionFragment, metric))
    );
  }

  for (const { key, counts, label } of aggs[facet].values) {
    if (isLimitedFacetKey(facets, facet as Facet, key)) {
      continue;
    } else {
      const row = [
        getTermLabel(facets[facet], key, label),
        ...metrics.map((metric) => String(counts[metric])),
      ];
      if (evolutionFragment) {
        metrics.forEach((metric) => {
          const evolution =
            evolutionFragment.data.aggs[facet].values.find(
              (value) => value.key === key
            )?.counts[metric] || "N/A";
          row.push(evolution.toString());
        });
      }
      table.push(row);
    }
  }
  return table.join("\n");
};

const formatPivotCard = (
  facets: Record<string, FormatedFacet>,
  fragment: SearchFragmentPivot
): BlobPart => {
  const aggs = fragment.data.aggs;
  const pivot = getTermFromAggs(fragment.request.aggs);
  const against = getTermFromAggs(fragment.request.aggs[pivot].aggs);
  const [metric] = fragment.request.aggs[pivot].metrics;

  const againstTerms: string[] = aggs[pivot].values.reduce((acc, value) => {
    value.aggs[against].values.forEach(({ key }) => {
      if (!acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, []);

  const againstTermsLabels = againstTerms.map((againstTerm) =>
    getTermLabel(facets[against], againstTerm)
  );

  const table: Table = [[facets?.[pivot]?.label, ...againstTermsLabels]];

  for (const { key, aggs: pivotAggs } of aggs[pivot].values) {
    const row = [getTermLabel(facets[pivot], key)];
    againstTerms.forEach((againstTerm) => {
      const count =
        pivotAggs[against].values.find((v) => v.key === againstTerm)?.counts?.[
          metric
        ] || 0;
      row.push(String(count));
    });
    table.push(row);
  }

  return table.join("\n");
};

export function formatCSV<T extends SearchFragment>(
  locale: Locale,
  facets: Record<string, FormatedFacet>,
  fragment: T,
  evolutionFragment?: T
): BlobPart {
  if (isSearchFragmentDateMetrics(fragment)) {
    return formatDateMetricsCard(
      locale,
      fragment,
      evolutionFragment as SearchFragmentDateMetrics
    );
  }
  if (isSearchFragmentDateTerm(fragment)) {
    return formatDateTermCard(locale, facets, fragment);
  }
  if (isSearchFragmentTerm(fragment)) {
    return formatTermOrClassificationCard(
      locale,
      facets,
      fragment,
      evolutionFragment as SearchFragmentTerm
    );
  }
  if (isSearchFragmentPivot(fragment)) {
    return formatPivotCard(facets, fragment);
  }
  if (isSearchFragmentGeo(fragment)) {
    return formatGeoCard(
      locale,
      facets,
      fragment,
      evolutionFragment as SearchFragmentGeo
    );
  }
  if (isSearchFragmentClassification(fragment)) {
    return formatTermOrClassificationCard(
      locale,
      facets,
      fragment,
      evolutionFragment as SearchFragmentClassification
    );
  } else {
    return "";
  }
}
